import React, {useMemo, useRef, useState, MouseEvent, useEffect} from 'react';
import AurAccordion, {
  AurAccordionRef,
} from '../../common/aur-accordion/AurAccordion';
import {Project, ProjectStatus} from './model';
import ProjectOverView from './ProjectOverView';
import {t} from 'i18next';
import {Box, Button, Typography} from '@mui/material';
import AurIcon, {IconNames} from '../../common/aur-icon/AurIcon';
import {pinUnpinProject} from '../../../api/UserManagementService';
import AurTooltip from '../../common/aur-tooltip/AurTooltip';
import {LaunchWorkspaceMenu} from './LaunchWorkspaceMenu';
import AurHighlyConfidential from '../../common/aur-highly-confidential/AurHighlyConfidential';
import {ReactComponent as InvestigativeIcon} from '../../../assests/svg/investigative.svg';
import AurButton from '../../common/aur-button/AurButton';
import {launchWorkspace} from '../../../api/LaunchPointService';
import AurSvgIcon from '../../common/aur-svg-icon/AurSvgIcon';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {
  setSelectedProjectId,
  setStartScroll,
} from '../../../redux/slices/projectOverviewSlice';
import AurCountryFlag from '../../common/aur-country-flag/AurCountryFlag';
import {getUTCDateTimeForDisplay} from '../../../utils/utils';
import ProjectStatusPill from './component/ProjectStatusPill';
import {useNavigate} from 'react-router-dom';
import {
  PROJECT_CREATION_PATH,
  setClientIdForProjectCreation,
  setProjectCreationInitiatedFrom,
} from '../../../redux/slices/projectCreationSlice';
import navConstants from '../../../constants/navConstants';
import {ONLY_DATE_SHOW_FORMAT} from '../../../constants/constants';
import {AurLightTooltip} from '../../common/aur-light-tooltip/aur-light-tooltip';

export interface IProps {
  projectDetails: Project;
  refreshProjectList: () => void;
  clientId: string;
  viewProjectName: string;
  onExpandproject?: () => void;
  expanded: boolean;
  onToggle: () => void;
}

const ProjectListItem: React.FC<IProps> = (props: IProps) => {
  const {projectDetails, refreshProjectList, clientId, viewProjectName} = props;
  const accordionRef = useRef<AurAccordionRef>(null);
  const [isPinned, setIsPinned] = useState<boolean>(projectDetails.isPinned);
  const [shouldShowLaunchWorkspaces, setShouldShowLaunchWorkspaces] =
    useState(false);
  const [launchWorkspacesAnchor, setLaunchWorkspacesAnchor] = useState<
    undefined | HTMLElement
  >(undefined);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const selectedProjectId = useSelector(
    (state: RootState) => state.projectOverview.selectedProjectId
  );

  const tooltipClasses = {
    arrow: 'aur-leftnav_tooltip-arrow',
    tooltip: 'aur-leftnav_tooltip-tooltip',
  };

  const pinUnpinProjects = async () => {
    setIsPinned(!isPinned);
    try {
      await pinUnpinProject(
        projectDetails.projectId,
        clientId,
        isPinned ? 'unpin' : 'pin'
      );
      refreshProjectList();
    } catch (error) {
      console.error('pinUnpinProjects', error);
    }
  };
  const handleLaunchWorkspaces = (event: MouseEvent<HTMLElement>) => {
    if (projectDetails.workspaces.length === 0) return;

    if (projectDetails.workspaces.length === 1) {
      launchWorkspace(
        projectDetails.workspaces[0].instanceId,
        projectDetails.workspaces[0].artifactID
      );
    } else {
      setLaunchWorkspacesAnchor(event.currentTarget);
      setShouldShowLaunchWorkspaces(true);
    }
  };

  const handleViewProgressClick = () => {
    dispatch(
      setProjectCreationInitiatedFrom(PROJECT_CREATION_PATH.ClientOverview)
    );
    const queryParam = {
      id: projectDetails?.projectId ?? '',
      name: projectDetails?.projectName ?? '',
    };
    dispatch(setClientIdForProjectCreation(clientId));
    navigate(
      navConstants.projectCreation.progressDetail +
        `?${new URLSearchParams(queryParam).toString()}`
    );
  };

  const closeLauncWorkspaces = () => {
    setShouldShowLaunchWorkspaces(false);
  };

  const isProjectClosed = useMemo(() => {
    return projectDetails.projectStatus == ProjectStatus.closed;
  }, [projectDetails]);
  const [offsetModifier, setOffsetModifier] = useState<[number, number]>([
    0, -6,
  ]);
  const [arrowPosition, setArrowPosition] = useState<string>('');

  const handleTooltipOpen = () => {
    setTimeout(() => {
      const tooltipEl =
        document.querySelector<HTMLElement>('.MuiTooltip-popper');
      if (tooltipEl) {
        const rect = tooltipEl.getBoundingClientRect();
        const rightSpace = window.innerWidth - rect.right;

        if (rightSpace < 50) {
          setArrowPosition('right');
          setOffsetModifier([-30, -6]);
        } else {
          setArrowPosition('');
          setOffsetModifier([0, -6]);
        }
      }
    }, 50);
  };

  const isLaunchPointMenuHidden = () => {
    if (isProjectClosed) {
      // Project is closed and we cannot even see the workspaces
      return true;
    }

    if (projectDetails.workspaces.length === 0) {
      // There are no workspaces so there is nothing to "Launch"
      return true;
    } else {
      // There are multiple workspaces so:
      // - menu is enabled if any workspaces are valid
      // - menu is diabled if all workspaces are invalid
      return !projectDetails.workspaces.some(
        (workspace) => workspace.userHasAccess
      );
    }
  };
  useEffect(() => {
    if (props.expanded) {
      accordionRef.current?.expand();
    } else {
      accordionRef.current?.collapse();
    }
  }, [props.expanded]);

  useEffect(() => {
    if (selectedProjectId === projectDetails.projectId) {
      accordionRef.current?.expand();
    }
  }, [selectedProjectId]);

  const isPendingProject = useMemo(() => {
    return projectDetails?.projectStatus === ProjectStatus.pending;
  }, [projectDetails]);
  const createdOnLabel = () => {
    if (isPendingProject) return t('common.project.submitted');
    else if (isRecentlyAddedProject) return t('common.project.added');
    return '';
  };

  const isRecentlyAddedProject = useMemo(() => {
    return projectDetails?.projectStatus === ProjectStatus.new;
  }, [projectDetails]);

  const titleAction = (
    <>
      {!(isPendingProject || isRecentlyAddedProject) && (
        <AurTooltip
          title={
            isPinned
              ? t('welcomeScreenSingleClient.unpinTooltip')
              : t('welcomeScreenSingleClient.pinTooltip')
          }
          disableInteractive
          classes={tooltipClasses}
          className="typo-text label3_size"
          arrowPosition={arrowPosition}
          slotProps={{
            popper: {
              modifiers: [{name: 'offset', options: {offset: offsetModifier}}],
            },
          }}
          onOpen={handleTooltipOpen}
        >
          <Button
            onClick={pinUnpinProjects}
            className={`pin-project-btn ${isPinned ? 'show' : ''} button-icon button-exit buttonMedium_Size`}
          >
            {isPinned ? (
              <AurIcon icon={IconNames.PushPin} />
            ) : (
              <AurIcon icon={IconNames.PushPinOutlined} />
            )}
          </Button>
        </AurTooltip>
      )}

      {projectDetails.highlyConfidential ? (
        <AurHighlyConfidential
          type="badge"
          className="project-header-highly-confidential"
        />
      ) : (
        <></>
      )}

      {(projectDetails?.userAlertCount ?? 0) > 0 && (
        <div className="welcome-single-alert-icon-container">
          <div className="alert-single-svg-container">
            <AurSvgIcon
              src="../assests/images/report-warning.svg"
              aria-label={t('welcomeScreenSingleClient.alert')}
            />
          </div>
          <div className="alert-single-count-container">{`${projectDetails.userAlertCount}`}</div>
        </div>
      )}

      {isPendingProject && (
        <AurButton
          endIcon={<AurIcon icon={IconNames.ChevronRight} />}
          classNames="project-creation-buttons"
          type="exit"
          size="Medium"
          label={t('common.project.viewProgress')}
          dataTestId="view-progress-button"
          onClick={handleViewProgressClick}
          ariaLabel={t('common.project.viewProgressHit')}
        />
      )}

      {!isLaunchPointMenuHidden() && (
        <AurButton
          endIcon={<AurIcon icon={IconNames.ChevronRight} />}
          type="exit"
          size="Medium"
          label={t('welcomeScreenSingleClient.launchWorkspace')}
          dataTestId="launch-workspace-button"
          onClick={handleLaunchWorkspaces}
        />
      )}
    </>
  );

  const isSubTitle =
    isPendingProject || isRecentlyAddedProject || projectDetails.ecaEnabled;
  const titleContent = (
    <>
      <Box
        className={`${isProjectClosed ? 'closed mist-700 ' : 'mist-1200'} d-inline-flex proj-title`}
      >
        <Typography className="typo-text subhead1_size">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              whiteSpace: 'pre',
            }}
          >
            {projectDetails.projectName}
            {projectDetails.ecaEnabled && (
              <AurCountryFlag
                countryCode={projectDetails?.domainCountryCode ?? ''}
                altText={projectDetails?.countryName ?? ''}
              />
            )}
          </Box>
        </Typography>
        {isProjectClosed && (
          <Box className="closed-chip">
            <AurIcon icon={IconNames.Block} size="small" />{' '}
            <Typography className="typo-text body2_size">
              {t('welcomeScreenSingleClient.closed')}
            </Typography>
          </Box>
        )}
      </Box>
      {isSubTitle && (
        <Box display="flex" alignItems="center" gap={1.5} marginTop={1.5}>
          {isPendingProject && <ProjectStatusPill statusType="pending" />}
          {isRecentlyAddedProject && (
            <ProjectStatusPill statusType="recentylAdded" />
          )}
          {(isPendingProject || isRecentlyAddedProject) && (
            <AurLightTooltip title={t('common.yearMonthDate')}>
              <Typography className="typo-text body2_size project-created-on-label">
                {t('common.project.createdOn', {
                  projectStatus: createdOnLabel(),
                  date: getUTCDateTimeForDisplay(
                    projectDetails?.createdOn ?? '',
                    ONLY_DATE_SHOW_FORMAT
                  ),
                })}
              </Typography>
            </AurLightTooltip>
          )}
          {projectDetails.ecaEnabled && (
            <Box className="eca-enable-wrapper">
              <Typography className="typo-text subhead3_size eca-enabled">
                {t('welcomeScreenSingleClient.ecaEnabled')}
              </Typography>
              {projectDetails.nlpSearchEnabled ? (
                <>
                  <InvestigativeIcon aria-hidden="true" />
                  <Typography className="typo-text body1_size investigative-search">
                    {t('welcomeScreenSingleClient.withInvestigativeSearch')}
                  </Typography>
                </>
              ) : (
                <Typography className="typo-text body1_size investigative-search">
                  {t('welcomeScreenSingleClient.withoutInvestigativeSearch')}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}
    </>
  );

  const startScroll = useSelector(
    (state: RootState) => state.projectOverview.startScroll
  );

  const selectedWorkspaceId = useSelector(
    (state: RootState) => state.projectOverview.selectedWorkspaceId
  );

  useEffect(() => {
    if (viewProjectName && viewProjectName === projectDetails.projectName) {
      accordionRef.current?.expand();
    }
  }, [viewProjectName, projectDetails.projectName]);

  const projectContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (
      selectedProjectId &&
      selectedProjectId == projectDetails.projectId &&
      !selectedWorkspaceId &&
      startScroll
    ) {
      dispatch(setSelectedProjectId(null));
      dispatch(setStartScroll(false));
      projectContainerRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [startScroll]);

  return (
    <div ref={projectContainerRef}>
      <AurAccordion
        onExpand={() => {
          props.onToggle();
          props.onExpandproject?.();
        }}
        onCollapse={() => {
          props.onToggle();
        }}
        showPointerCursorWithinTitle
        ref={accordionRef}
        fullWidth
        collapsed={true}
        title={titleContent}
        titleContent={titleAction}
        alertWarningIcon={false}
        toggleButtonTestId="project-accordion-toggle"
        className={`${isProjectClosed ? 'project-closed' : ''} ${projectDetails.ecaEnabled ? 'custom-accordion' : ''}`}
        hideToggleButton={isPendingProject}
        enableAutoScrollOnExpand={true}
      >
        {!isPendingProject ? (
          <ProjectOverView
            updateAlertCount={refreshProjectList}
            projectDetails={projectDetails}
            isProjectClosed={isProjectClosed}
          />
        ) : undefined}
      </AurAccordion>
      <LaunchWorkspaceMenu
        show={shouldShowLaunchWorkspaces}
        workspaces={projectDetails.workspaces}
        anchor={launchWorkspacesAnchor}
        onClose={closeLauncWorkspaces}
      />
    </div>
  );
};

export default ProjectListItem;
